import React from 'react';
import { Link } from 'react-router-dom';
import type { RecentDocumentItem } from '../../api/documents';
import { formatRelativeTimeRo } from '../../utils/dates';
import { avatarGradient } from '../member/memberAvatar';
import { memberDisplayInitials } from '../../utils/memberDisplay';

interface RecentActivityFeedProps {
  items: RecentDocumentItem[];
  /** When `false`, hide technical pipeline-status chips and only show
   *  a 3-state human label (Procesat / În procesare / Eroare). */
  isAdvanced: boolean;
}

/**
 * Classify a pipeline_status token into one of three simple UI buckets.
 * Used both to render the chip variant AND to pick the user-facing
 * label in simplified view.
 */
function statusBucket(p: string | null | undefined): 'ok' | 'processing' | 'error' | 'none' {
  if (!p) return 'none';
  if (p === 'completed') return 'ok';
  if (p.includes('error') || p === 'failed') return 'error';
  return 'processing';
}

const ADVANCED_LABELS: Record<string, string> = {
  queued: 'În coadă',
  processing_started: 'Procesare inițiată',
  loading_images: 'Încărcare imagini',
  ocr_extraction: 'OCR în curs',
  processing: 'Procesare avansată',
  summary_generating: 'Rezumat AI în curs',
  classification_generating: 'Clasificare în curs',
  summary_completed: 'Rezumat finalizat',
  classification_completed: 'Clasificare finalizată',
  completed: 'Procesat',
  error: 'Eroare',
  failed: 'Eșuat',
  summary_error: 'Eroare la rezumat',
  classification_error: 'Eroare la clasificare',
};

const SIMPLE_LABELS: Record<'ok' | 'processing' | 'error' | 'none', string> = {
  ok: 'Procesat',
  processing: 'În procesare',
  error: 'Eroare',
  none: 'În așteptare',
};

const IconDot = () => <span className="dash-feed-status-dot" aria-hidden />;

const IconFile = () => (
  <svg
    width="14"
    height="14"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden
  >
    <path d="M14.5 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7.5L14.5 2z" />
    <polyline points="14 2 14 8 20 8" />
  </svg>
);

const RecentActivityFeed: React.FC<RecentActivityFeedProps> = ({ items, isAdvanced }) => {
  return (
    <section className="dash-panel dash-feed" aria-labelledby="dash-feed-title">
      <div className="dash-panel-head">
        <h2 className="dash-panel-title" id="dash-feed-title">
          Activitate recentă
        </h2>
        <span className="dash-panel-sub">Ultimele {items.length} documente</span>
      </div>
      {items.length === 0 ? (
        <div className="dash-feed-empty">
          <p>Niciun document încărcat încă.</p>
          <Link to="/families" className="btn btn-secondary btn-sm">
            Mergi la familii
          </Link>
        </div>
      ) : (
        <ul className="dash-feed-list">
          {items.map((doc) => {
            const bucket = statusBucket(doc.pipeline_status);
            const chipLabel = isAdvanced
              ? ADVANCED_LABELS[doc.pipeline_status || ''] || SIMPLE_LABELS[bucket]
              : SIMPLE_LABELS[bucket];
            const rel = formatRelativeTimeRo(doc.upload_date);
            const initials = memberDisplayInitials({ name: doc.member_name });
            const gradient = avatarGradient(doc.member_id);
            return (
              <li key={doc.id} className="dash-feed-item">
                <div className="dash-feed-row">
                  <Link
                    to={`/documents/${doc.id}`}
                    className="dash-feed-row-cover"
                    aria-label={`Deschide documentul ${doc.original_filename} pentru ${doc.member_name}`}
                  />
                  <span
                    className="dash-feed-avatar"
                    style={{ background: gradient }}
                    aria-hidden
                  >
                    {initials}
                  </span>
                  <div className="dash-feed-main">
                    <div
                      className="dash-feed-filename"
                      title={doc.original_filename}
                    >
                      {doc.original_filename}
                    </div>
                    <div className="dash-feed-meta">
                      <Link
                        to={`/members/${doc.member_id}`}
                        className="dash-feed-chip dash-feed-chip-member"
                      >
                        {doc.member_name}
                      </Link>
                      {doc.family_name && (
                        <span className="dash-feed-sep" aria-hidden>·</span>
                      )}
                      {doc.family_name && (
                        <span className="dash-feed-family" title={doc.family_name}>
                          {doc.family_name}
                        </span>
                      )}
                      {doc.document_types && doc.document_types.length > 0 && (
                        <>
                          <span className="dash-feed-sep" aria-hidden>·</span>
                          <span className="dash-feed-type">
                            <IconFile />
                            {doc.document_types.join(', ')}
                          </span>
                        </>
                      )}
                    </div>
                  </div>
                  <div className="dash-feed-side">
                    <span
                      className={`dash-feed-status dash-feed-status--${bucket}`}
                      role={bucket === 'processing' ? 'status' : undefined}
                    >
                      <IconDot />
                      {chipLabel}
                    </span>
                    {rel && <span className="dash-feed-time">{rel}</span>}
                  </div>
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
};

export default RecentActivityFeed;
